import { Button } from 'react-bootstrap';
import { Table } from 'Components/organisms';
import { useContext, useMemo, useState, useEffect, useCallback } from 'react';
import {
    AmpSettingDto,
    FilterCriteria,
    FindingSpecDto,
    FindingSpecKind,
    JAMF_SMART_GROUPS_SETTINGS,
    JamfSmartGroupDto,
    JamfSmartGroupsSetting,
    KIND,
} from '@ampsec/platform-client';
import { SMART_GROUPS_CONFIG_COLUMNS } from './SmartGroupsConfigColumns';
import FindingsModal from '../FindingsConfigTable/FindingsModal';
import { ConfirmationModal } from 'Components/molecules/ConfirmationModal';
import {
    addFindingSpecs,
    getFindingSpecs,
    getJamfConnectorSetting,
    updateFindings,
    updateSettings,
} from 'Apis/library';
import {
    DEFAULT_ERROR_MSG,
    INTEGRATIONHEADER,
    NEVER,
    REFRESH,
    SEARCH_PLACEHOLDER,
} from 'Core-utils/constants/constants';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { Typography } from 'Components/atoms';
import moment from 'moment';
import { useI18n } from 'Hooks/useI18n';
import { SearchBar } from 'Components/molecules';
import { Status } from 'Core-utils/helpers/helpers';
import './styles.scss';

interface SmartGroupConfigTableProps {
    cid: string;
    filter?: FilterCriteria;
}

interface subHeaderContentProps {
    buttonLabel: string;
    disabled: boolean;
    searchInputValue: string;
    onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onClickHandler?: React.MouseEventHandler<HTMLButtonElement>;
}

const SubHeaderContent = ({
    buttonLabel,
    disabled,
    searchInputValue,
    onSearchChange,
    onClickHandler,
}: subHeaderContentProps) => {
    return (
        <div className="d-flex justify-content-between w-100 p-0">
            <SearchBar
                value={searchInputValue}
                onChange={onSearchChange}
                placeholder={SEARCH_PLACEHOLDER}
                searchClassName="search-container__input-cont"
            />
            <Button className="bg-primary-500 body1 ps-4 pe-4 pt-2 pb-2" onClick={onClickHandler} disabled={disabled}>
                {buttonLabel}
            </Button>
        </div>
    );
};

const ROW_PER_PAGE_OPTIONS = [10, 20, 30, 40];

// --- EXTERNAL HELPERS TO RESOLVE LINT ERRORS ---
const addToLoadingSet = (key: string) => (prev: Set<string>) => {
    const next = new Set(prev);
    next.add(key);
    return next;
};

const removeFromLoadingSet = (key: string) => (prev: Set<string>) => {
    const next = new Set(prev);
    next.delete(key);
    return next;
};

const updateStatusMap = (key: string, status: string) => (prev: Record<string, string>) => ({
    ...prev,
    [key]: status,
});

const removeStatusFromMap = (key: string) => (prev: Record<string, string>) => {
    const next = { ...prev };
    delete next[key];
    return next;
};

const SmartGroupsConfigTable = ({ cid }: SmartGroupConfigTableProps) => {
    const [selectedSmartGroupForAction, setSelectedSmartGroupForAction] = useState<JamfSmartGroupDto | null>(null);
    const [pendingStatusUpdates, setPendingStatusUpdates] = useState<Record<string, string>>({});
    const [loadingRows, setLoadingRows] = useState<Set<string>>(new Set());
    const [showDeactivationConfirmation, setShowDeactivationConfirmation] = useState<boolean>(false);

    const [show, setShow] = useState<boolean>(false);
    const [_refreshData, setRefreshData] = useState<boolean>(false);
    const [findingSpecData, setFindingSpecData] = useState<FindingSpecDto>({} as FindingSpecDto);
    const [isFindingSpecLoading, setIsFindingSpecLoading] = useState<boolean>(true);
    const [findingSpecsMap, setFindingSpecsMap] = useState<Record<string, FindingSpecDto>>({});

    const { addToast } = useContext(ToastsContext);
    const [searchValue, setSearchValue] = useState<string>('');
    const { t } = useI18n();

    const { data: jamfConnectorSettings, isLoading, refresh } = useApiObjectData<
        { [key: string]: AmpSettingDto },
        { [key: string]: AmpSettingDto }
    >(
        'JAMF_CONNECTOR_SETTINGS',
        () => getJamfConnectorSetting(cid),
        (a) => a,
    );

    const SmartGroupData = (jamfConnectorSettings?.[JAMF_SMART_GROUPS_SETTINGS]
        ?.value as unknown) as JamfSmartGroupsSetting;

    // Keep findingSpecsMap fresh whenever _refreshData toggles (same trigger as V1's refresh pattern)
    useEffect(() => {
        const loadFindingSpecs = async () => {
            try {
                const specs = await getFindingSpecs(KIND.FINDING_SPECS, {});
                if (specs && Array.isArray(specs)) {
                    const map: Record<string, FindingSpecDto> = {};
                    specs.forEach((spec) => {
                        if (spec.name) {
                            map[spec.name] = spec;
                        }
                    });
                    setFindingSpecsMap(map);
                }
            } catch (error) {
                addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
            }
        };
        loadFindingSpecs();
    }, [_refreshData]);

    useEffect(() => {
        const groups = SmartGroupData?.groups ?? [];
        setPendingStatusUpdates((prev) => {
            const updated = { ...prev };
            for (const g of groups) {
                const key = `${g.groupType}_${g.groupId}`;
                if (updated[key] === g.status) {
                    delete updated[key];
                }
            }
            return updated;
        });
    }, [SmartGroupData?.groups]);

    // V1 logic: updateSetting is the single path for all setting writes
    const updateSetting = useCallback(
        (value: unknown) => {
            const existingSetting = jamfConnectorSettings?.[JAMF_SMART_GROUPS_SETTINGS] as AmpSettingDto;
            if (existingSetting) {
                updateSettings(existingSetting.id, {
                    id: existingSetting.id,
                    key: JAMF_SMART_GROUPS_SETTINGS,
                    value: value,
                } as AmpSettingDto).then(() => refresh());
            }
        },
        [jamfConnectorSettings, refresh],
    );

    // V1 logic: refresh button sends REFRESH sentinel to backend
    const handleRefresh = () => {
        updateSetting(REFRESH);
    };

    // V1 logic: handleSave is purely for the FindingsModal save action
    const handleSave = async (finding: FindingSpecDto) => {
        if (finding.id) {
            await updateFindings({ ...finding, kind: FindingSpecKind.CUSTOM })
                .then(() => setRefreshData((refreshData) => !refreshData))
                .catch(() => addToast({ severity: 'error', body: DEFAULT_ERROR_MSG }));
        } else {
            await addFindingSpecs({ ...finding, kind: FindingSpecKind.CUSTOM }).then(() =>
                setRefreshData((refreshData) => !refreshData),
            );
        }
    };

    // V1 logic: handleEditClick makes a live API call for the finding spec
    const handleEditClick = async (row: JamfSmartGroupDto) => {
        if (row.status === Status.ENABLED) {
            setShow(true);
            if (!row.groupId) {
                return;
            }
            setIsFindingSpecLoading(true);

            getFindingSpecs(KIND.FINDING_SPECS, {
                name: `JAMF_SMART_GROUP_${row.groupType}_${row.groupId}`,
            })
                .then((res) => {
                    setFindingSpecData((res?.[0] ?? {}) as FindingSpecDto);
                })
                .finally(() => {
                    setIsFindingSpecLoading(false);
                });
        }
    };

    // V2 addition: toggle handler replaces the V1 dropdown onChange
    const handleStatusChange = (row: JamfSmartGroupDto, isChecked: boolean) => {
        const rowKey = `${row.groupType}_${row.groupId}`;
        if (loadingRows.has(rowKey)) {
            return;
        }
        setSelectedSmartGroupForAction(row);

        if (isChecked) {
            // Enable: use V1's updateSetting directly, same as the old RoleDropdown path
            const newStatus = Status.ENABLED;
            setPendingStatusUpdates(updateStatusMap(rowKey, newStatus));
            setLoadingRows(addToLoadingSet(rowKey));
            updateSetting({ ...row, status: newStatus });
            // Optimistically clear loading after setting is sent (refresh() in updateSetting handles data sync)
            setLoadingRows(removeFromLoadingSet(rowKey));
            setSelectedSmartGroupForAction(null);
        } else {
            // Disable: show confirmation first (V2 addition)
            setPendingStatusUpdates(updateStatusMap(rowKey, Status.DISABLED));
            setShowDeactivationConfirmation(true);
        }
    };

    // V2 addition: confirm deactivation uses V1's updateSetting
    const handleConfirmDeactivation = async () => {
        if (!selectedSmartGroupForAction) {
            return;
        }
        const rowKey = `${selectedSmartGroupForAction.groupType}_${selectedSmartGroupForAction.groupId}`;
        try {
            setLoadingRows(addToLoadingSet(rowKey));
            updateSetting({ ...selectedSmartGroupForAction, status: Status.DISABLED });
            setShowDeactivationConfirmation(false);
            setRefreshData((p) => !p);
        } catch {
            setPendingStatusUpdates(removeStatusFromMap(rowKey));
            addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
        } finally {
            setLoadingRows(removeFromLoadingSet(rowKey));
            setSelectedSmartGroupForAction(null);
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
    };

    const groupsData = useMemo(() => {
        const groups = SmartGroupData?.groups ?? [];
        if (!groups.length) {
            return [];
        }
        const normalizedSearch = searchValue.trim().toLowerCase();
        const statusPriority: Record<string, number> = {
            [Status.ENABLED]: 2,
            [Status.DISABLED]: 1,
        };

        return groups
            .filter((item) => item.groupName.toLowerCase().includes(normalizedSearch))
            .slice()
            .sort((a, b) => {
                const statusDiff = (statusPriority[b.status] ?? 0) - (statusPriority[a.status] ?? 0);
                if (statusDiff !== 0) {
                    return statusDiff;
                }
                return a.groupName.localeCompare(b.groupName);
            });
    }, [SmartGroupData?.groups, searchValue]);

    const tableColumns = useMemo(
        () =>
            SMART_GROUPS_CONFIG_COLUMNS(
                handleEditClick,
                handleStatusChange,
                findingSpecsMap,
                loadingRows,
                pendingStatusUpdates,
            ),
        [handleEditClick, handleStatusChange, findingSpecsMap, loadingRows, pendingStatusUpdates],
    );

    if (!jamfConnectorSettings?.[JAMF_SMART_GROUPS_SETTINGS]) {
        return (
            <Typography variant="subtitle3" className="text-text-high-emphasis">
                {t('NO_JAMF_SMART_GROUP_SETTINGS')}
            </Typography>
        );
    }

    const buttonLabel =
        SmartGroupData?.syncStatus === 'SYNCING' ? 'Syncing JAMF Smart Groups' : 'Refresh JAMF Smart Groups';

    return (
        <div className="smart-group">
            <div className="d-flex align-items-center gap-1 mb-2">
                <Typography variant="subtitle3" color="text-medium-emphasis">
                    {INTEGRATIONHEADER[1]}
                </Typography>
                <Typography variant="body5" color="text-medium-emphasis">
                    {SmartGroupData?.lastSynced ? moment(SmartGroupData?.lastSynced).fromNow() : NEVER}
                </Typography>
            </div>
            <Table
                cacheKey={'findingSpecs'}
                data={groupsData}
                dataIsLoading={isLoading}
                columns={tableColumns}
                rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
                transformer={(a) => a}
                subHeaderVariant="header-with-refresh-button"
                subHeaderContent={
                    <SubHeaderContent
                        buttonLabel={buttonLabel}
                        disabled={SmartGroupData?.syncStatus === 'SYNCING'}
                        searchInputValue={searchValue}
                        onSearchChange={handleSearchChange}
                        onClickHandler={handleRefresh}
                    />
                }
                tableBodyHeight="25rem"
                onRowClicked={handleEditClick}
                pagination={false}
                defaultSortId="status"
                defaultSortOrder="DESC"
            />
            <FindingsModal
                show={show}
                onClose={() => {
                    setShow(false);
                    setFindingSpecData({} as FindingSpecDto);
                    setSelectedSmartGroupForAction(null);
                }}
                onSave={handleSave}
                defaultFindingType={findingSpecData}
                isDataLoading={isFindingSpecLoading}
            />
            <ConfirmationModal
                show={showDeactivationConfirmation}
                onClose={() => {
                    setShowDeactivationConfirmation(false);
                    if (selectedSmartGroupForAction) {
                        const key = `${selectedSmartGroupForAction.groupType}_${selectedSmartGroupForAction.groupId}`;
                        setPendingStatusUpdates(removeStatusFromMap(key));
                    }
                    setSelectedSmartGroupForAction(null);
                }}
                modalTitle="Deactivate Smart Group"
                primaryButtonLabel="Confirm Deactivation"
                buttonVariant="danger"
                onPrimaryButtonClick={handleConfirmDeactivation}
                modalBodyTitle="Are you sure you want to deactivate this Smart Group?"
            />
        </div>
    );
};

export default SmartGroupsConfigTable;
